// src/components/CertificateButton.js
import React, { useState, useEffect } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import Button from 'components/Form/Button';
import { parseStrapiImage } from 'utils';

// --- Utils ---
async function loadPdfBytesStrict(url) {
  const res = await fetch(url);
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) throw new Error(`HTTP ${res.status} ao buscar ${url} (content-type: ${ct})`);
  const buf = await res.arrayBuffer();
  const view = new Uint8Array(buf.slice(0, 5));
  const header = String.fromCharCode(...view);
  if (!header.startsWith('%PDF-')) {
    let snippet = '';
    try {
      snippet = new TextDecoder().decode(new Uint8Array(buf).slice(0, 180));
    } catch { }
    throw new Error(`Conteúdo não é PDF em ${url}. ct=${ct}. Trecho: ${snippet}`);
  }
  return buf;
}

async function prepareOutDocWithTemplate(tplBytes, pageIndex) {
  const srcDoc = await PDFDocument.load(tplBytes);
  const outDoc = await PDFDocument.create();
  const [copiedPage] = await outDoc.copyPages(srcDoc, [pageIndex]);
  outDoc.addPage(copiedPage);
  return { outDoc, page: copiedPage };
}

function drawTextLine(page, font, { text, x, y, size, align, color }) {
  const width = font.widthOfTextAtSize(text, size);
  const realX = align === 'center' ? x - width / 2 : x;
  page.drawText(text, { x: realX, y, size, font, color: color || rgb(0, 0, 0) });
}

function drawTextBox(
  page,
  font,
  { text, x, y, size, boxWidth, align = 'left', lineHeight = size * 1.2, color }
) {
  const lines = wrapText({
    text,
    maxWidth: boxWidth,
    measure: (t) => font.widthOfTextAtSize(t, size),
  });
  let cursorY = y;
  for (const line of lines) {
    const w = font.widthOfTextAtSize(line, size);
    const lineX = align === 'center' ? x - w / 2 : x;
    page.drawText(line, { x: lineX, y: cursorY, size, font, color: color || rgb(0, 0, 0) });
    cursorY -= lineHeight;
  }
  return cursorY;
}

function drawBulletList(page, font, { items, x, y, size, lineHeight, bullet, boxWidth }) {
  let cursorY = y;
  for (const item of items) {
    const bulletWithSpace = `${bullet} `;
    const bw = font.widthOfTextAtSize(bulletWithSpace, size);
    const lines = wrapText({
      text: item,
      maxWidth: boxWidth - bw,
      measure: (t) => font.widthOfTextAtSize(t, size),
    });
    if (lines[0]) {
      page.drawText(bulletWithSpace + lines[0], {
        x,
        y: cursorY,
        size,
        font,
        color: rgb(0, 0.16862745098039217, 0.27058823529411763),
      });
      cursorY -= lineHeight;
    }
    for (let i = 1; i < lines.length; i++) {
      page.drawText('  ' + lines[i], {
        x,
        y: cursorY,
        size,
        font,
        color: rgb(0, 0.16862745098039217, 0.27058823529411763),
      });
      cursorY -= lineHeight;
    }
  }
  return cursorY;
}

function sanitizeData(data) {
  const lang = data.language ?? 'pt';
  const safeStudentName = String(data.studentName ?? '');
  const safeCourseName = String(data.courseName ?? (data.title ?? ''));
  const safeTopics = Array.isArray(data.topics) ? data.topics.map((t) => String(t ?? '')) : [];
  const safeSigners = Array.isArray(data.signers)
    ? data.signers.map((s) => ({ name: String(s?.name ?? ''), title: String(s?.title ?? '') }))
    : [];

  const safeEndDate = data.dateTo ? String(data.dateTo) : '';
  const safeInstructor = data?.instructor?.name ? {
    name: String(data.instructor.name),
    signatureUrl: data.instructor.signature?.url
      ? parseStrapiImage(data.instructor.signature.url)
      : '',
    signatureMime: String(data.instructor.signature?.mime || ''),
  } : null;

  return {
    lang,
    safeStudentName,
    safeCourseName,
    safeTopics,
    safeSigners,
    safeEndDate,
    safeInstructor,
  };
}

function buildDurationText(data, lang) {
  if (!data) return '';
  if (data.durationText) return String(data.durationText);

  if (data.durationHours != null) {
    const value = Number(data.durationHours);
    if (Number.isNaN(value)) return '';

    if (lang === 'pt') {
      return `Carga horária: ${value} hora${value === 1 ? '' : 's'}`;
    }
    return `Course duration: ${value} hour${value === 1 ? '' : 's'}`;
  }

  return '';
}

// Nome do aluno
function drawHeaderSection(page, font, L, safeStudentName) {
  if (L.student.boxWidth) {
    drawTextBox(page, font, {
      text: safeStudentName,
      x: L.student.x,
      y: L.student.y,
      size: L.student.size,
      boxWidth: L.student.boxWidth,
      align: L.student.align,
      color: L.student.color,
    });
  } else {
    drawTextLine(page, font, {
      text: safeStudentName,
      x: L.student.x,
      y: L.student.y,
      size: L.student.size,
      align: L.student.align,
      color: L.student.color,
    });
  }
}

// Nome do curso
function drawCourse(page, font, L, courseName) {
  const cfg = L.course;
  if (!cfg || !courseName) return;

  if (cfg.boxWidth) {
    drawTextBox(page, font, {
      text: courseName,
      x: cfg.x,
      y: cfg.y,
      size: cfg.size,
      boxWidth: cfg.boxWidth,
      align: cfg.align || 'left',
      color: cfg.color,
    });
  } else {
    drawTextLine(page, font, {
      text: courseName,
      x: cfg.x,
      y: cfg.y,
      size: cfg.size,
      align: cfg.align || 'left',
      color: cfg.color,
    });
  }
}

// Carga horária / duração
function drawDuration(page, font, L, durationText) {
  const cfg = L.duration;
  if (!cfg || !durationText) return;

  if (cfg.boxWidth) {
    drawTextBox(page, font, {
      text: durationText,
      x: cfg.x,
      y: cfg.y,
      size: cfg.size,
      boxWidth: cfg.boxWidth,
      align: cfg.align || 'left',
      color: cfg.color,
    });
  } else {
    drawTextLine(page, font, {
      text: durationText,
      x: cfg.x,
      y: cfg.y,
      size: cfg.size,
      align: cfg.align || 'left',
      color: cfg.color,
    });
  }
}

function drawCompletionStatement(page, font) {
  drawTextBox(page, font, {
    text: 'has successfully completed the training program, with a module duration of',
    x: 225,
    y: 376,
    size: 16,
    boxWidth: 600,
    lineHeight: 20,
    color: rgb(0, 0.16862745098039217, 0.27058823529411763),
  });
}

// Data final (vem do layout)
function drawDateField(page, font, L, endDateText) {
  const cfg = L.date;
  if (!cfg || !endDateText) return;

  const fullText = cfg.prefix ? `${cfg.prefix}${endDateText}` : endDateText;

  if (cfg.boxWidth) {
    drawTextBox(page, font, {
      text: fullText,
      x: cfg.x,
      y: cfg.y,
      size: cfg.size,
      boxWidth: cfg.boxWidth,
      align: cfg.align || 'left',
      color: cfg.color,
    });
  } else {
    drawTextLine(page, font, {
      text: fullText,
      x: cfg.x,
      y: cfg.y,
      size: cfg.size,
      align: cfg.align || 'left',
      color: cfg.color,
    });
  }
}

function drawTopicsSection(page, font, model, L, safeTopics) {
  const mid = Math.ceil((safeTopics || []).length / 2);
  const col1 = (safeTopics || []).slice(0, mid);
  const col2 = (safeTopics || []).slice(mid);

  const INNER_PADDING = 12;
  const firstLineY = L.topicsCol1.y - 40;

  const leftEdge = Math.min(L.topicsCol1.x, L.topicsCol2?.x ?? L.topicsCol1.x) - INNER_PADDING;
  const rightEdge =
    Math.max(
      L.topicsCol1.x + L.topicsCol1.boxWidth,
      (L.topicsCol2?.x ?? L.topicsCol1.x) + (L.topicsCol2?.boxWidth ?? 0)
    ) + INNER_PADDING;
  const boxWidth = Math.max(0, rightEdge - leftEdge);

  const titleSize = L.topicsCol1.size + 4;
  const topY = firstLineY + L.topicsCol1.size + INNER_PADDING;

  const { width: pageWidth } = page.getSize();
  const centerX = (pageWidth - boxWidth) / 2;
  const titleY = topY;

  drawTextLine(page, font, {
    text: 'Learning overview',
    x: centerX + INNER_PADDING,
    y: titleY,
    size: titleSize,
    align: 'left',
    color: rgb(0, 0.16862745098039217, 0.27058823529411763),
  });

  const col1X = centerX + INNER_PADDING;
  const col2X = centerX + boxWidth / 2 + INNER_PADDING;
  const startY = titleY - 30;

  drawBulletList(page, font, {
    items: col1,
    x: col1X,
    y: startY,
    size: L.topicsCol1.size,
    lineHeight: L.topicsCol1.lineHeight,
    bullet: L.topicsCol1.bullet,
    boxWidth: L.topicsCol1.boxWidth,
    color: rgb(0, 0.16862745098039217, 0.27058823529411763),
  });

  drawBulletList(page, font, {
    items: col2,
    x: col2X,
    y: startY,
    size: L.topicsCol2.size,
    lineHeight: L.topicsCol2.lineHeight,
    bullet: L.topicsCol2.bullet,
    boxWidth: L.topicsCol2.boxWidth,
    color: rgb(0, 0.16862745098039217, 0.27058823529411763),
  });
}

async function drawInstructor(page, outDoc, font, L, instructor) {
  const cfg = L.instructor;
  if (!cfg || !instructor?.name) return;

  if (instructor.signatureUrl) {
    const response = await fetch(instructor.signatureUrl);
    if (!response.ok) {
      throw new Error(`Unable to load the instructor signature (HTTP ${response.status}).`);
    }
    const blob = await response.blob();
    const normalizedMime = (instructor.signatureMime || blob.type).toLowerCase();
    let image;
    if (normalizedMime.includes('png')) {
      image = await outDoc.embedPng(await blob.arrayBuffer());
    } else if (normalizedMime.includes('jpeg') || normalizedMime.includes('jpg')) {
      image = await outDoc.embedJpg(await blob.arrayBuffer());
    } else {
      image = await outDoc.embedPng(await convertImageBlobToPng(blob));
    }
    const scale = Math.min(cfg.signatureWidth / image.width, cfg.signatureHeight / image.height);
    page.drawImage(image, {
      x: cfg.x,
      y: cfg.signatureY,
      width: image.width * scale,
      height: image.height * scale,
    });
  }

  drawTextLine(page, font, {
    text: `Instructor: ${instructor.name}`,
    x: cfg.x,
    y: cfg.nameY,
    size: cfg.size,
    align: 'left',
    color: cfg.color,
  });
}

async function convertImageBlobToPng(blob) {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const source = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('The instructor signature image format is not supported.'));
      image.src = objectUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = source.naturalWidth;
    canvas.height = source.naturalHeight;
    const context = canvas.getContext('2d');
    context.drawImage(source, 0, 0);
    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        result => result ? resolve(result) : reject(new Error('Unable to convert the instructor signature.')),
        'image/png'
      );
    });
    return pngBlob.arrayBuffer();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function saveAndDownload(outDoc, fileName) {
  console.log(fileName);

  return outDoc.save().then((bytes) => {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    return url;
  });
}

function wrapText(args) {
  const { text, maxWidth, measure } = args;
  const words = text.split(/\s+/);
  const lines = [];
  let current = '';

  for (const w of words) {
    const test = current ? current + ' ' + w : w;
    if (measure(test) <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines;
}

const layoutByModel = {
  1: {
    student: { x: 225, y: 420, size: 32, align: 'start', color: rgb(0, 0.6157, 0.6902), font: 'Gilroy', },
    duration: { x: 225, y: 360, size: 16, align: 'start', color: rgb(0, 0.16862745098039217, 0.27058823529411763), font: 'Gilroy-Regular', },
    course: { x: 225, y: 320, size: 26, align: 'start', color: rgb(0, 0.16862745098039217, 0.27058823529411763), font: 'Gilroy', boxWidth: 600, },
    date: { x: 600, y: 105, size: 11, align: 'start', color: rgb(0, 0.16862745098039217, 0.27058823529411763), font: 'Gilroy-Regular', },
    instructor: { x: 600, signatureY: 48, nameY: 31, signatureWidth: 140, signatureHeight: 45, size: 11, color: rgb(0, 0.16862745098039217, 0.27058823529411763), font: 'Gilroy-Regular' },

    topicsCol1: { x: 410, y: 310, size: 10, lineHeight: 17, align: 'start', bullet: '', color: rgb(0, 0.16862745098039217, 0.27058823529411763), boxWidth: 280, font: 'Gilroy-Regular' },
    topicsCol2: { x: 525, y: 310, size: 10, lineHeight: 17, align: 'start', bullet: '', color: rgb(0, 0.16862745098039217, 0.27058823529411763), boxWidth: 280, font: 'Gilroy-Regular' },
  },
};

async function getOrLoadFont(outDoc, cache, fontName) {
  if (!fontName) return cache._default;

  if (cache[fontName]) return cache[fontName];

  if (fontName === 'Helvetica') {
    return cache._default;
  }

  const url = `/fonts/${fontName}.ttf`;
  const res = await fetch(url);
  if (!res.ok) {
    console.warn(`Falha ao carregar fonte "${fontName}" em ${url}, usando Helvetica.`);
    return cache._default;
  }
  const bytes = await res.arrayBuffer();
  const font = await outDoc.embedFont(bytes);
  cache[fontName] = font;
  return font;
}

export default function CertificateButton({
  model = 1,
  data,
  fileName = 'certificate.pdf',
  templateUrl = '/cert-templates/new-exemplo.pdf',
  onBeforeGenerate,
  onAfterGenerate,
  disabled,
  label = 'Generate Certificate',
  mode = 'prod',
  buttonColor = 'primary',
  buttonWidth = 'fit-content',
}) {
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClick = async () => {
    try {
      setBusy(true);
      if (onBeforeGenerate) await onBeforeGenerate();

      const tplBytes = await loadPdfBytesStrict(templateUrl);
      console.log('[Cert] template loaded, bytes:', tplBytes.byteLength);

      const pageIndex = model === 1 ? 0 : 1;
      const { outDoc, page } = await prepareOutDocWithTemplate(tplBytes, pageIndex);
      console.log('[Cert] outDoc prepared, page copied');

      if (outDoc.registerFontkit) {
        outDoc.registerFontkit(fontkit);
      } else {
        console.warn(
          '[Cert] Este pdf-lib não expõe registerFontkit; fontes customizadas podem não funcionar.'
        );
      }

      const defaultFont = await outDoc.embedFont(StandardFonts.Helvetica);
      const fontsCache = { _default: defaultFont };

      const L = layoutByModel[model];
      const { lang, safeStudentName, safeCourseName, safeTopics, safeEndDate, safeInstructor } =
        sanitizeData(data);

      const durationText = buildDurationText(data, lang);

      const studentFont = await getOrLoadFont(outDoc, fontsCache, L.student.font);
      const courseFont = await getOrLoadFont(outDoc, fontsCache, L.course && L.course.font);
      const durationFont = await getOrLoadFont(outDoc, fontsCache, L.duration && L.duration.font);
      const dateFont = await getOrLoadFont(outDoc, fontsCache, L.date && L.date.font);
      const instructorFont = await getOrLoadFont(outDoc, fontsCache, L.instructor && L.instructor.font);
      const bodyFont = defaultFont;

      drawCompletionStatement(page, durationFont || bodyFont);
      // Nome do aluno
      drawHeaderSection(page, studentFont, L, safeStudentName);
      // Carga horária
      drawDuration(page, durationFont || bodyFont, L, durationText);
      // Data final (só usa a string que você passou em data.dateTo)
      drawDateField(page, dateFont || bodyFont, L, safeEndDate);
      // Nome do curso
      drawCourse(page, courseFont || bodyFont, L, safeCourseName);
      // Tópicos
      drawTopicsSection(page, bodyFont, model, L, safeTopics);
      await drawInstructor(page, outDoc, instructorFont || bodyFont, L, safeInstructor);

      console.log('[Cert] drawing finished, saving...');

      let url = null;

      if (mode === 'dev') {
        const bytes = await outDoc.save();
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const newUrl = URL.createObjectURL(blob);

        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return newUrl;
        });

        url = newUrl;
      } else {
        url = await saveAndDownload(outDoc, fileName);
      }

      if (onAfterGenerate && url) onAfterGenerate(url);
    } catch (err) {
      console.error('Erro ao gerar certificado:', err);
      const msg = err?.message || String(err);
      alert('Falha ao gerar certificado:\n' + msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Button
        onClick={handleClick}
        disabled={disabled || busy}
        width={buttonWidth}
        color={buttonColor}
        nospace
      >
        {busy ? 'Generating…' : label || 'Generate Certificate'}
      </Button>

      {mode === 'dev' && previewUrl && (
        <div style={{ marginTop: 24, position: 'fixed', left: 0, right: 500, top: 0 }}>
          <iframe
            src={previewUrl}
            title='Preview do certificado'
            style={{
              width: '100%',
              height: '800px',
              border: '1px solid #ccc',
              borderRadius: '8px',
            }}
          />
        </div>
      )}
    </div>
  );
}
